import React, { useState, useEffect } from 'react';
import { X, Copy, Check, MessageCircle, FileText } from 'lucide-react';
import { generateMessage } from '../utils/data';
import { useToast } from '../contexts/ToastContext';
import { applyScriptToPatient } from './ScriptManager';

export default function MessagePanel({ patient, practiceName, onClose, onMarkContacted }) {
  const [copied, setCopied] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [useScript, setUseScript] = useState(true); // CHANGED: Default to true (My Script first)
  const [scriptMessage, setScriptMessage] = useState('');
  const { addToast } = useToast();
  
  // Get the default message from the old system
  const defaultMessage = generateMessage(patient, practiceName);
  
  // When useScript is toggled, generate the script-based message
  useEffect(() => {
    if (useScript) {
      // Get the user's selected script from localStorage or use a default
      const savedScript = localStorage.getItem('recallready_selected_script');
      if (savedScript) {
        try {
          const script = JSON.parse(savedScript);
          const personalizedLine = patient.personalizedLine || `We've been thinking about you and wanted to check in.`;
          const message = applyScriptToPatient(script, patient, practiceName, personalizedLine);
          setScriptMessage(message);
        } catch (e) {
          setScriptMessage(defaultMessage);
        }
      } else {
        // If no script is saved, use a default script
        const defaultScript = {
          template: `Hi {firstName},

{personalizedLine}

This is {practiceName} reaching out. It's been {monthsSinceLastVisit} months since your last visit, and we'd love to see you for your {treatmentType} follow-up.

When works best for you? Give us a call or reply here!`
        };
        const personalizedLine = patient.personalizedLine || `We've been thinking about you and wanted to check in.`;
        const message = applyScriptToPatient(defaultScript, patient, practiceName, personalizedLine);
        setScriptMessage(message);
      }
    }
  }, [useScript, patient, practiceName, defaultMessage]);

  const message = useScript ? scriptMessage : defaultMessage;

  function handleCopy() {
    navigator.clipboard.writeText(message).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      setShowConfirm(true);
      addToast('Message copied to clipboard');
    });
  }

  function handleWhatsApp() {
    const phone = patient.phone ? patient.phone.replace(/\D/g, '') : '';
    if (!phone) {
      addToast('No phone number on file', 'error');
      return;
    }
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setShowConfirm(true);
  }

  function handleMarkContacted() {
    onMarkContacted(patient.id);
    onClose();
  }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 15, color: '#1B2B4B' }}>
              Message for {patient.firstName} {patient.lastName}
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#7A7A72', marginTop: 2 }}>
              {patient.phone || 'No phone on file'} · {patient.treatmentType}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7A7A72' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
          {/* Toggle between script and default */}
          <div style={toggleContainerStyle}>
  <button
    onClick={() => setUseScript(true)}
    style={{
      ...toggleBtnStyle,
      background: useScript ? '#2D9B8A' : 'transparent',
      color: useScript ? '#FFFFFF' : '#7A7A72',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    }}
  >
    <FileText size={14} />
    My Script  {/* ← NOW FIRST */}
  </button>
  <button
    onClick={() => setUseScript(false)}
    style={{
      ...toggleBtnStyle,
      background: !useScript ? '#2D9B8A' : 'transparent',
      color: !useScript ? '#FFFFFF' : '#7A7A72',
    }}
  >
    Default Message  {/* ← NOW SECOND */}
  </button>
</div>

          {useScript && patient.personalizedLine && (
            <div style={personalizedBadgeStyle}>
              💡 Personalization: "{patient.personalizedLine}"
            </div>
          )}

          <label style={labelStyle}>Message preview</label>
          <div style={messageBoxStyle}>
            {message}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={handleCopy} style={copyBtnStyle}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy message'}
            </button>
            <button onClick={handleWhatsApp} style={waBtnStyle} disabled={!patient.phone}>
              <MessageCircle size={14} />
              Open WhatsApp
            </button>
          </div>

          {showConfirm && (
            <div style={confirmBoxStyle}>
              <p style={{ margin: '0 0 12px 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: '#1B2B4B', fontWeight: 600 }}>
                Mark {patient.firstName} as contacted?
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleMarkContacted} style={yesBtn}>Yes, mark contacted</button>
                <button onClick={() => setShowConfirm(false)} style={noBtn}>No, not yet</button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 28, padding: '16px', background: '#F7F7F5', border: '1px solid #E5E5E0', borderRadius: 2 }}>
            <p style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#9A9A92', lineHeight: 1.5 }}>
              {useScript 
                ? `This message uses your custom script with ${patient.firstName}'s personalized line.` 
                : 'Copy the message and send via your preferred SMS tool, or use the WhatsApp button to open a pre-filled chat.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Styles
const overlayStyle = {
  position: 'fixed', 
  inset: 0, 
  background: 'rgba(0,0,0,0.35)', 
  zIndex: 1000,
  display: 'flex', 
  justifyContent: 'flex-end',
};

const panelStyle = {
  background: '#fff', 
  width: 440, 
  maxWidth: '100vw', 
  height: '100vh',
  display: 'flex', 
  flexDirection: 'column', 
  boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
};

const headerStyle = {
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'flex-start',
  padding: '20px 28px', 
  borderBottom: '1px solid #E5E5E0',
};

const labelStyle = {
  display: 'block', 
  fontSize: 11, 
  fontWeight: 700, 
  textTransform: 'uppercase',
  letterSpacing: '0.5px', 
  color: '#1B2B4B', 
  marginBottom: 8,
  fontFamily: "'JetBrains Mono', monospace",
};

const messageBoxStyle = {
  background: '#FAFAF8', 
  border: '1px solid #DDDDD8', 
  borderRadius: 2,
  padding: '16px', 
  fontSize: 13, 
  lineHeight: 1.7, 
  color: '#2A2A25',
  fontFamily: "'JetBrains Mono', monospace", 
  whiteSpace: 'pre-wrap',
  minHeight: 120,
};

const copyBtnStyle = {
  display: 'flex', 
  alignItems: 'center', 
  gap: 6,
  padding: '10px 16px', 
  background: '#1B2B4B', 
  color: '#fff',
  border: 'none', 
  borderRadius: 2, 
  cursor: 'pointer', 
  flex: 1,
  fontSize: 13, 
  fontWeight: 600, 
  fontFamily: "'JetBrains Mono', monospace",
};

const waBtnStyle = {
  display: 'flex', 
  alignItems: 'center', 
  gap: 6,
  padding: '10px 16px', 
  background: '#25D366', 
  color: '#fff',
  border: 'none', 
  borderRadius: 2, 
  cursor: 'pointer', 
  flex: 1,
  fontSize: 13, 
  fontWeight: 600, 
  fontFamily: "'JetBrains Mono', monospace",
};

const confirmBoxStyle = {
  marginTop: 20, 
  padding: '16px', 
  background: '#FEF6EE',
  border: '1px solid #F9D5AE', 
  borderRadius: 2,
};

const yesBtn = {
  padding: '9px 14px', 
  background: '#2D9B8A', 
  color: '#fff',
  border: 'none', 
  borderRadius: 2, 
  cursor: 'pointer',
  fontSize: 12, 
  fontWeight: 600, 
  fontFamily: "'JetBrains Mono', monospace",
};

const noBtn = {
  padding: '9px 14px', 
  background: '#fff', 
  color: '#1B2B4B',
  border: '1px solid #DDDDD8', 
  borderRadius: 2, 
  cursor: 'pointer',
  fontSize: 12, 
  fontWeight: 600, 
  fontFamily: "'JetBrains Mono', monospace",
};

const toggleContainerStyle = {
  display: 'flex',
  background: '#F5F5F2',
  borderRadius: 6,
  padding: 4,
  marginBottom: 16,
};

const toggleBtnStyle = {
  flex: 1,
  padding: '6px 12px',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "'JetBrains Mono', monospace",
  transition: 'all 0.2s',
};

const personalizedBadgeStyle = {
  background: '#F0F7F6',
  border: '1px solid #B5DED9',
  borderRadius: 4,
  padding: '8px 12px',
  fontSize: 12,
  color: '#2D9B8A',
  fontFamily: "'JetBrains Mono', monospace",
  marginBottom: 12,
  fontStyle: 'italic',
};